import {
  IDocumentRevisionData,
  IQuoteDocumentTagData,
  IQuoteProductDocumentData
} from '@/models';
import {
  IDocumentService,
  IFetchQuoteProductDocumentsParams,
  IQuoteProductDocumentPaginatedListData,
  ISetQuoteProductDocumentTagsPayload
} from './document.types';
import { AxiosInstance } from 'axios';
import { objectToFormData } from '@/utils/objectToFormData';

export class DocumentService implements IDocumentService {
  private endpoint: string;

  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/quotes/product-documents/';
  }

  fetchQuoteProductDocuments(
    params: IFetchQuoteProductDocumentsParams
  ): Promise<IQuoteProductDocumentPaginatedListData> {
    //By default axios converts list parameters to 'key[]=value'... this is not compatible with django.
    //this is why we use URLSearchParams to convert the list to 'key=value'
    //TODO MG 11-10-2024 Find a way to simplify or create a generic function
    const queryParams = new URLSearchParams({
      search: params.search ?? '',
      page: String(params.page ?? 1)
    });
    params.quote_document__document__tags?.forEach(tag => {
      queryParams.append('quote_document__document__tags', String(tag));
    });

    return this.axios
      .get<IQuoteProductDocumentPaginatedListData>(`${this.endpoint}`, {
        params: queryParams
      })
      .then(({ data }) => data);
  }

  fetchQuoteProductDocument(id: number): Promise<IQuoteProductDocumentData> {
    return this.axios
      .get<IQuoteProductDocumentData>(`${this.endpoint}${id}/`)
      .then(({ data }) => data);
  }

  createQuoteProductDocument(
    data: IQuoteProductDocumentData
  ): Promise<IQuoteProductDocumentData> {
    const formData = objectToFormData(data);

    return this.axios
      .post<IQuoteProductDocumentData>(`${this.endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then(({ data }) => data);
  }

  updateQuoteProductDocument(
    data: IQuoteProductDocumentData
  ): Promise<IQuoteProductDocumentData> {
    const formData = objectToFormData(data);

    return this.axios
      .patch<IQuoteProductDocumentData>(
        `${this.endpoint}${data.id}/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )
      .then(({ data }) => data);
  }

  deleteQuoteProductDocument(id: number): Promise<any> {
    return this.axios.delete(`${this.endpoint}${id}/`);
  }

  fetchDocumentRevisions(id: number): Promise<IDocumentRevisionData[]> {
    return this.axios
      .get<IDocumentRevisionData[]>(`v1/documents/${id}/revisions/`)
      .then(({ data }) => data);
  }

  //Tags
  fetchQuoteDocumentTags(): Promise<IQuoteDocumentTagData[]> {
    return this.axios
      .get<IQuoteDocumentTagData[]>(`v1/quotes/document-tags/`)
      .then(({ data }) => data);
  }

  createQuoteDocumentTag(
    data: IQuoteDocumentTagData
  ): Promise<IQuoteDocumentTagData> {
    return this.axios
      .post<IQuoteDocumentTagData>(`v1/quotes/document-tags/`, data)
      .then(({ data }) => data);
  }

  updateQuoteDocumentTag(
    data: IQuoteDocumentTagData
  ): Promise<IQuoteDocumentTagData> {
    return this.axios
      .patch<IQuoteDocumentTagData>(`v1/quotes/document-tags/${data.id}/`, data)
      .then(({ data }) => data);
  }

  deleteQuoteDocumentTag(id: number): Promise<any> {
    return this.axios.delete(`v1/quotes/document-tags/${id}/`);
  }

  setQuoteProductDocumentTags(
    data: ISetQuoteProductDocumentTagsPayload
  ): Promise<IQuoteProductDocumentData> {
    return this.axios
      .put<IQuoteDocumentTagData>(`${this.endpoint}${data.quoteId}/set-tags/`, {
        tags: data.tags
      })
      .then(({ data }) => data);
  }
}
